import {
  AzureRelayRegion,
  BackendType,
  CDNProvider,
  InfraBackendInput,
  InfraCDNSlot,
  InfraRelayInput,
  InfraWireGuardInput,
  InfrastructureCreatePayload,
  InfrastructureRequest,
} from "../../services/apiService";
import {
  defaultRelayProfileForType,
  normalizeRelayProfile,
} from "./terraformPreview";

const CDN_PROVIDERS = new Set<CDNProvider>(["azure", "frontdoor", "fastly"]);

const BACKEND_TYPES = new Set([
  "morpheus",
  "mythic",
  "cobaltstrike",
  "teams",
  "c3",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asTenantID(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallback;
}

function asCDNProvider(value: unknown): CDNProvider | null {
  let raw = asString(value).toLowerCase().trim();
  if (raw === "azure_frontdoor") raw = "frontdoor";
  return CDN_PROVIDERS.has(raw as CDNProvider) ? (raw as CDNProvider) : null;
}

/** Parse wizard-array or terraform-map CDN slots; expand legacy count. */
export function parseCDNSlots(
  value: unknown,
  legacyCount?: unknown,
): InfraCDNSlot[] {
  if (Array.isArray(value)) {
    const slots: InfraCDNSlot[] = [];
    for (const item of value) {
      const row = asRecord(item);
      if (!row) continue;
      const provider = asCDNProvider(row.provider);
      if (!provider) continue;
      const fixed = asString(row.domain_fixed).trim().toLowerCase();
      slots.push(fixed ? { provider, domain_fixed: fixed } : { provider });
    }
    if (slots.length > 0) return slots;
  }

  const asMap = asRecord(value);
  if (asMap) {
    const keys = Object.keys(asMap)
      .map((k) => Number(k))
      .filter((k) => Number.isFinite(k) && k > 0)
      .sort((a, b) => a - b);
    const slots: InfraCDNSlot[] = [];
    for (const key of keys) {
      const row = asRecord(asMap[String(key)]);
      if (!row) continue;
      const provider = asCDNProvider(row.provider);
      if (!provider) continue;
      const fixed = asString(row.domain_fixed).trim().toLowerCase();
      slots.push(fixed ? { provider, domain_fixed: fixed } : { provider });
    }
    if (slots.length > 0) return slots;
  }

  const count =
    typeof legacyCount === "number" && Number.isFinite(legacyCount)
      ? Math.max(0, Math.floor(legacyCount))
      : 0;
  return Array.from({ length: count }, () => ({ provider: "azure" as const }));
}

/** Human-readable tenant label for UI (API sugar, then terraform_json). */
export function tenantLabelFromInfrastructureRequest(
  row: InfrastructureRequest,
): string {
  if (row.tenant_provider) return row.tenant_provider;

  const fromRequest = asRecord(row.request_json);
  const fromRequestSugar = asString(fromRequest?.tenant_provider);
  if (fromRequestSugar) return fromRequestSugar;

  const doc = asRecord(row.terraform_json);
  const client = asRecord(doc?.client) || (() => {
    if (!doc) return null;
    const key =
      Object.keys(doc).find((k) => asRecord(doc[k])) || row.client_key || "1";
    return asRecord(doc[key]);
  })();
  const fromTerraform = asString(client?.tenant_provider);
  if (fromTerraform) return fromTerraform;

  return row.tenant_id ? String(row.tenant_id) : "";
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function asRegion(value: unknown): AzureRelayRegion {
  if (
    value === "europe" ||
    value === "america_south" ||
    value === "america_north" ||
    value === "asia" ||
    value === "middle_east" ||
    value === "africa"
  ) {
    return value;
  }
  // Legacy drafts used northamerica
  if (value === "northamerica") {
    return "america_north";
  }
  return "europe";
}

function withBackendProfiles(
  backends: InfraBackendInput[],
  relays: InfraRelayInput[],
): InfraBackendInput[] {
  return backends.map((backend, index) => {
    const backendIndex = index + 1;
    const fromBackend = normalizeRelayProfile(backend.relay_profile_type);
    if (fromBackend) {
      return { ...backend, relay_profile_type: fromBackend };
    }
    const linked = relays.find(
      (r) =>
        !r.standalone &&
        r.connected_to_backend === backendIndex &&
        !!normalizeRelayProfile(r.relay_profile_type),
    );
    if (linked?.relay_profile_type) {
      return {
        ...backend,
        relay_profile_type: normalizeRelayProfile(linked.relay_profile_type),
      };
    }
    const def = defaultRelayProfileForType(backend.type);
    return def ? { ...backend, relay_profile_type: def } : { ...backend };
  });
}

/** Build a wizard draft from a stored infrastructure request (for modify → new submission). */
export function draftFromInfrastructureRequest(
  row: InfrastructureRequest,
): InfrastructureCreatePayload {
  const fromRequest = hydrateFromRequestJson(row.request_json, row);
  if (fromRequest) return fromRequest;
  const fromTerraform = hydrateFromTerraformJson(row.terraform_json, row);
  if (fromTerraform) return fromTerraform;

  return {
    project_reference: row.project_reference,
    project_codename: "",
    owner: "",
    created_by: "",
    azure_relay_region: "europe",
    project_team: [],
    tenant_id: row.tenant_id,
    webhook_url: "",
    channel_name: "",
    client_key: row.client_key || "1",
    backends: [],
    relays: [],
    wireguards: [{}],
  };
}

function hydrateFromRequestJson(
  raw: unknown,
  row: InfrastructureRequest,
): InfrastructureCreatePayload | null {
  const data = asRecord(raw);
  if (!data) return null;
  if (!Array.isArray(data.backends) && !Array.isArray(data.wireguards)) {
    return null;
  }

  const relays: InfraRelayInput[] = Array.isArray(data.relays)
    ? data.relays
        .map((r) => asRecord(r))
        .filter((r): r is Record<string, unknown> => !!r)
        .map((r) => ({
          type: asString(r.type, "relay"),
          connected_to_backend:
            typeof r.connected_to_backend === "number"
              ? r.connected_to_backend
              : 0,
          relay_profile_type:
            normalizeRelayProfile(asString(r.relay_profile_type)) || undefined,
          domain: asString(r.domain) || undefined,
          cdn_slots: parseCDNSlots(r.cdn_slots, r.cdn_domains_count),
          azure_relay_location: asString(r.azure_relay_location) || undefined,
          standalone: !!r.standalone || asString(r.type) === "relay",
        }))
    : [];

  const backendsRaw: InfraBackendInput[] = Array.isArray(data.backends)
    ? data.backends
        .map((b) => asRecord(b))
        .filter((b): b is Record<string, unknown> => !!b)
        .map((b) => ({
          type: (asString(b.type, "mythic").toLowerCase() ||
            "mythic") as BackendType,
          relay_profile_type:
            normalizeRelayProfile(asString(b.relay_profile_type)) || undefined,
        }))
        .filter((b) => BACKEND_TYPES.has(b.type))
    : [];

  const backends = withBackendProfiles(backendsRaw, relays);

  const wireguards: InfraWireGuardInput[] = Array.isArray(data.wireguards)
    ? data.wireguards
        .map((w) => asRecord(w))
        .filter((w): w is Record<string, unknown> => !!w)
        .map((w) => ({
          azure_relay_location: asString(w.azure_relay_location) || undefined,
        }))
    : [{}];

  return {
    project_reference:
      asString(data.project_reference) ||
      asString(data.project_code) ||
      row.project_reference,
    project_codename: asString(data.project_codename),
    owner: asString(data.owner),
    created_by: asString(data.created_by),
    azure_rg_location: asString(data.azure_rg_location) || "westeurope",
    azure_relay_region: asRegion(data.azure_relay_region),
    project_team: asStringArray(data.project_team),
    tenant_id: asTenantID(data.tenant_id, row.tenant_id),
    webhook_url: asString(data.webhook_url),
    channel_name: asString(data.channel_name),
    client_key: asString(data.client_key) || row.client_key || "1",
    backends,
    relays,
    wireguards: wireguards.length > 0 ? wireguards : [{}],
  };
}

function hydrateFromTerraformJson(
  raw: unknown,
  row: InfrastructureRequest,
): InfrastructureCreatePayload | null {
  const doc = asRecord(raw);
  if (!doc) return null;

  // New shape: { client_id, shared_state_key, client: { ... } }
  // Legacy shape: { "<client_key>": { ... } }
  let clientKey = row.client_key || "1";
  let client = asRecord(doc.client);
  if (client) {
    if (typeof doc.client_id === "number") {
      clientKey = String(doc.client_id);
    } else if (typeof doc.client_id === "string" && doc.client_id.trim()) {
      clientKey = doc.client_id.trim();
    }
  } else {
    clientKey =
      Object.keys(doc).find((k) => asRecord(doc[k])) || row.client_key || "1";
    client = asRecord(doc[clientKey]);
  }
  if (!client) return null;

  const backendsMap = asRecord(client.c2_backend) || {};
  const backendsRaw: InfraBackendInput[] = Object.keys(backendsMap)
    .map((k) => Number(k))
    .filter((k) => Number.isFinite(k) && k > 0)
    .sort((a, b) => a - b)
    .map((k) => {
      const entry = asRecord(backendsMap[String(k)]);
      const type = asString(entry?.type).toLowerCase() as BackendType;
      return {
        type: BACKEND_TYPES.has(type) ? type : ("mythic" as BackendType),
      };
    });

  const relaysMap = asRecord(client.relays) || {};
  const relays: InfraRelayInput[] = [];
  const wireguards: InfraWireGuardInput[] = [];

  Object.keys(relaysMap)
    .map((k) => Number(k))
    .filter((k) => Number.isFinite(k))
    .sort((a, b) => a - b)
    .forEach((k) => {
      const entry = asRecord(relaysMap[String(k)]);
      if (!entry) return;
      const type = asString(entry.type).toLowerCase();
      if (type === "wireguard") {
        wireguards.push({
          azure_relay_location:
            asString(entry.azure_relay_location) || undefined,
        });
        return;
      }
      const connected =
        typeof entry.connected_to_backend === "number"
          ? entry.connected_to_backend
          : 0;
      relays.push({
        type,
        connected_to_backend: connected,
        relay_profile_type:
          normalizeRelayProfile(asString(entry.relay_profile_type)) ||
          undefined,
        domain: asString(entry.domain) || undefined,
        cdn_slots: parseCDNSlots(entry.cdn_slots, entry.cdn_domains_count),
        azure_relay_location:
          asString(entry.azure_relay_location) || undefined,
        standalone: type === "relay" || connected === 0,
      });
    });

  return {
    project_reference:
      asString(client.project_code) || row.project_reference,
    project_codename: asString(client.project_codename),
    owner: asString(client.owner),
    created_by: asString(client.created_by),
    azure_rg_location: asString(client.azure_rg_location) || "westeurope",
    azure_relay_region: asRegion(client.azure_relay_region),
    project_team: asStringArray(client.project_team),
    tenant_id: row.tenant_id,
    webhook_url: asString(client.webhook_url),
    channel_name: asString(client.channel_name),
    client_key: clientKey,
    backends: withBackendProfiles(backendsRaw, relays),
    relays,
    wireguards: wireguards.length > 0 ? wireguards : [{}],
  };
}
